"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateDonationMatchAmountPerNonprofitValue = exports.calculateDonationMatchAmountValue = void 0;
const number_1 = require("../codecs/number");
const types_1 = require("../entity/types");
/**
 * Calculates the match amount
 *
 * @param campaign: a donation match campaign object as returned to the frontend
 *        Includes:
 *        - availableAmountPerNonprofit: a mapping from nonprofit id to available
 *          to that nonprofit.
 *          If a nonprofit id is not in the mapping, it means there's no limit for it.
 *        - availableForMatching: safe int. the amount available for matching in the
 *          campaign.
 * @param donationChargeAmount: the max amount to match the donation charge
 * @param toNonprofitId: the nonprofit id the donation is for.
 *        Used to check the available amount to match that exact nonprofit.
 * @param frequency: the donation's frequency
 * @param paymentMethod: the donation's payment method
 * @param matchAmountRemainingInCampaign: the available amount in the campaign
 *        without considering pending recurring matches and the donor's previous matches.
 *        It should be <= to campaign.availableForMatching.
 *        Backend only parameter.
 * @param matchesConsumed: boolean flag indicating if the matches have been consumed
 *        for the user / donation.
 *        This happens when a recurring donation has consumed its recurring matches
 *        or the user has been matched enough times before.
 *        Backend only parameter.
 * @param chargePayToNonprofits: the donation charge's pay to nonprofits
 * @param chargePayToNonprofitWeights: the donation charge's pay to nonprofit weights
 * @param previousPayToNonprofitMatches: the matches per nonprofit that
 *        where applied to previous charges from the same donation.
 *        Backend only parameter.
 * @param returnError: a boolean flag indicating if we want null values when there's
 *        and error or if we want the errored values for processing.
 *        Useful to log the error on the backend.
 *        There should be no errors in the frontend.
 *        Errors could be related to race conditions when depleting the matching funds.
 *        Backend only parameter.
 *
 * @returns
 */
function calculateDonationMatchAmountValue({ campaign, donationChargeAmount, toNonprofitId, frequency, paymentMethod, matchAmountRemainingInCampaign, matchesConsumed, chargePayToNonprofits, chargePayToNonprofitWeights, previousPayToNonprofitMatches, returnError, }) {
    const maxMatchPerDonation = frequency === types_1.DonationFrequency.ONCE
        ? campaign.maxMatchPerDonationOneTimeAmount
        : campaign.maxMatchPerDonationRecurringAmount;
    const matchMultipler = frequency === types_1.DonationFrequency.ONCE
        ? campaign.matchMultiplierOneTime
        : campaign.matchMultiplierRecurring;
    const donationMatchAmount = (0, number_1.coerceToSafeIntOrThrow)({
        num: Math.min(campaign.availableForMatching, donationChargeAmount * matchMultipler, matchAmountRemainingInCampaign ?? Infinity, campaign.maxPerUserMatchAmount ?? Infinity, maxMatchPerDonation ?? Infinity),
    });
    const available = campaign.availableAmountPerNonprofit;
    const maxMatchFromNonprofitMatches = available && chargePayToNonprofits
        ? chargePayToNonprofits.reduce((acum, id) => acum + (available[id] ?? campaign.availableForMatching), 0)
        : undefined;
    const maxMatchingAvailable = Math.min(campaign.availableForMatching, campaign.maxPerUserMatchAmount, maxMatchPerDonation ?? campaign.availableForMatching, maxMatchFromNonprofitMatches ?? campaign.availableForMatching);
    if (donationMatchAmount <= 0 && paymentMethod === types_1.PaymentMethod.CRYPTO) {
        return {
            donationMatchAmount: null,
            donationChargeMatchAmount: null,
            payToNonprofitMatches: undefined,
            maxMatchingAvailable,
        };
    }
    if (donationMatchAmount < 0) {
        if (returnError) {
            return {
                donationMatchAmount: donationMatchAmount,
                donationChargeMatchAmount: donationMatchAmount,
                payToNonprofitMatches: undefined,
                maxMatchingAvailable,
            };
        }
        return {
            donationMatchAmount: null,
            donationChargeMatchAmount: null,
            payToNonprofitMatches: undefined,
            maxMatchingAvailable,
        };
    }
    const donationChargeMatchAmount = (!donationMatchAmount || matchesConsumed ? 0 : donationMatchAmount);
    const payToNonprofitMatches = calculateDonationMatchAmountPerNonprofitValue({
        campaign,
        maxChargeTotalMatch: donationMatchAmount,
        chargePayToNonprofits,
        chargePayToNonprofitWeights,
        previousPayToNonprofitMatches,
    });
    const totalPayToNonprofitMatches = payToNonprofitMatches && payToNonprofitMatches.length > 0
        ? payToNonprofitMatches.reduce((acum, x) => acum + x, 0)
        : undefined;
    const toNonprofitMaxAmount = toNonprofitId &&
        (campaign.availableAmountPerNonprofit || {})[toNonprofitId];
    return {
        donationMatchAmount: Math.min(donationMatchAmount, totalPayToNonprofitMatches || donationMatchAmount, toNonprofitMaxAmount || donationMatchAmount),
        donationChargeMatchAmount: Math.min(donationChargeMatchAmount, totalPayToNonprofitMatches || donationChargeMatchAmount, toNonprofitMaxAmount || donationChargeMatchAmount),
        payToNonprofitMatches,
        maxMatchingAvailable,
    };
}
exports.calculateDonationMatchAmountValue = calculateDonationMatchAmountValue;
/**
 * Calculates the match amount per nonprofit
 *
 * @param campaign: a donation match campaign object as returned to the frontend
 *        includes availableAmountPerNonprofit:
 *        a mapping from nonprofit id to available to that nonprofit.
 *        If a nonprofit id is not in the mapping, it means there's no limit for it.
 * @param maxChargeTotalMatch: the max amount to match the donation charge
 * @param chargePayToNonprofits: the donation charge's pay to nonprofits
 * @param chargePayToNonprofitWeights: the donation charge's pay to nonprofit weights
 * @param previousPayToNonprofitMatches: the matches per nonprofit that
 *        where applied to previous charges from the same donation.
 *
 * @returns
 */
function calculateDonationMatchAmountPerNonprofitValue({ campaign, maxChargeTotalMatch, chargePayToNonprofits, chargePayToNonprofitWeights, previousPayToNonprofitMatches, }) {
    // Define match per nonprofit when it applies
    let payToNonprofitMatches = undefined;
    if (campaign.availableAmountPerNonprofit &&
        Object.entries(campaign.availableAmountPerNonprofit).length) {
        // If the per nonprofit matches have already been calculated use those values
        // to avoid re-calculating the available amount per nonprofit which are already
        // considering them
        if (previousPayToNonprofitMatches) {
            payToNonprofitMatches = previousPayToNonprofitMatches;
        }
        else {
            const payToNonprofitWeights = chargePayToNonprofitWeights ?? chargePayToNonprofits?.map((_) => 1);
            if (campaign.availableAmountPerNonprofit &&
                chargePayToNonprofits &&
                payToNonprofitWeights) {
                payToNonprofitMatches = [];
                const totalWeight = payToNonprofitWeights.reduce((acum, w) => acum + w, 0);
                if (totalWeight === 0) {
                    return payToNonprofitMatches;
                }
                const rawMatches = payToNonprofitWeights.map((weight) => Math.floor((maxChargeTotalMatch * weight) / totalWeight));
                const totalRawMatch = rawMatches.reduce((acum, w) => acum + w, 0);
                // Avoid floating point division / multiplication resulting in a lost match amount
                if (Math.abs(totalRawMatch - maxChargeTotalMatch) > 0) {
                    rawMatches[0] += Math.abs(totalRawMatch - maxChargeTotalMatch);
                }
                for (let i = 0; i < chargePayToNonprofits.length; i++) {
                    const nonprofitId = chargePayToNonprofits[i];
                    const rawMatch = rawMatches[i];
                    const available = campaign.availableAmountPerNonprofit[nonprofitId];
                    const match = (Math.max(0, Math.min(rawMatch, available ?? rawMatch)) || 0);
                    payToNonprofitMatches.push(match);
                }
            }
        }
    }
    return payToNonprofitMatches;
}
exports.calculateDonationMatchAmountPerNonprofitValue = calculateDonationMatchAmountPerNonprofitValue;
//# sourceMappingURL=donationMatch.js.map